import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Dimensions, StyleSheet, Text, View } from 'react-native';
import useMedia from "use-media";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../language/i18n'

import '../../../src/style/css/Hp.css';
import { hpContext } from './HPContext';
import SeoHeadTag from '../common/seo/SeoHeadTag';
import HpHeader from "./header/HpHeader";
import HpFooter from './footer/HpFooter';
import ChangeLanguage from '../common/language/ChangeLanguage';

import {
  routeTop, routeTopJa, routeTopPt, routeTopEs,
  routeTopKo, routeTopHi, routeTopPl,
  routeAppApi,
  routeTermOfService, routePrivacyPolicy, routeTokutei,
  routeContact, routeContactDone,
} from '../../style/css/CmnStyle';
import Loading from '../loading/Loading';
import Top from './routes/top/Top';
import TermOfService from './routes/term_of_service/TermOfService';
import PrivacyPolicy from './routes/privacy_policy/PrivacyPolicy';
import Tokutei from './routes/tokutei/Tokutei';
import Contact from './routes/contact/Contact';
import ContactDone from './routes/contact/ContactDone';
import Error404 from './routes/err/Error404';


// Analytics: Google Analytics(react-ga4)は2026-09-12に廃止（Cookie同意バナー不要のCloudflare Web Analyticsへ移行）。
// 呼び出し元多数（hpContext.ReactGA.event(...)）を個別修正せずに済むよう、同じインターフェースのno-opスタブに置き換える。
const ReactGA = { event: () => {} };


// URLが言語を明示しているトップページ（/ja 等）はそのURL通りの言語を優先し、
// 端末の言語による自動リダイレクトの対象から除外する
const PATH_LANG_MAP = {
  [routeTopJa]: 'ja',
  [routeTopPt]: 'pt',
  [routeTopEs]: 'es',
  [routeTopKo]: 'ko',
  [routeTopHi]: 'hi',
  [routeTopPl]: 'pl',
};


// 翻訳データの読み込みを待つ上限（ミリ秒）。過ぎたら同梱の英語で表示する
const LANG_LOAD_TIMEOUT_MS = 1500;


// サイトが対応している言語
const SUPPORTED_LANGS = ['ja', 'en', 'pt', 'es', 'ko', 'hi', 'pl'];


// 端末（ブラウザ）の言語設定から表示言語を決める
// 2026-10-07: IPジオロケーション（ipapi.co）は応答待ちに上限が無く、遅延・失敗時にページが読み込み中のまま
// ダウンロードボタンが出ない恐れがあったため廃止。端末の言語は通信なしで即座に取得できる
const getBrowserLang = () => {
  const langs = (navigator.languages && navigator.languages.length > 0)
    ? navigator.languages
    : [navigator.language];

  // 端末の優先順に見て、最初に対応している言語を使う（'ja-JP' → 'ja'）
  for(const lang of langs) {
    if(!lang) continue;
    const code = lang.toLowerCase().split('-')[0];
    if(SUPPORTED_LANGS.includes(code)) {
      return code;
    }
  }

  // デフォルト：英語
  return 'en';
}


// 画面サイズ取得
const getWindowDimensions = () => {
  const width = document.documentElement.clientWidth;
  const { innerHeight: height } = window;
  return {
    width,
    height
  };
}


function Hp() {
  // ヘッダーの高さ
  const scrollHeadHeight = '8vh';
  // PC/SP判定
  const isWide = useMedia('(min-width: 780px)');
  // 画面サイズ
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
  // 文字サイズ調整(ポルトガル語・スペイン語の場合小さくする)
  const [isTxtSmall, setIsTxtSmall] = useState(false);

  // 現在のパス
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();

  // 言語設定(en/ja)
  const [langCode, setLangCode] = useState();
  const { t, i18n } = useTranslation();

  const [isLangLoading, setIsLangLoading] = useState(true);


  // iOSダウンロードボタンがタップされたとき
  function onClickIosDownload() {
    // ログ：イベント
    ReactGA.event("eventTapIosDownload");
  }
  // Androidダウンロードボタンがタップされたとき
  function onClickAndroidDownload() {
    // ログ：イベント
    ReactGA.event("eventTapAndroidDownload");
  }

  const contextValue = {
    ReactGA: ReactGA,
    scrollHeadHeight: scrollHeadHeight,
    isWide: isWide,
    windowDimensions: windowDimensions,
    langCode: langCode,
    setLangCode: setLangCode,
    isTxtSmall: isTxtSmall,
    iosLink: 'https://apps.apple.com/app/skaters-tool/id6450068697?itsct=apps_box_link&itscg=30200',
    androidLink: 'https://play.google.com/store/apps/details?id=com.skaters.app',
    onClickIosDownload: onClickIosDownload,
    onClickAndroidDownload: onClickAndroidDownload,
  }


  useEffect(() => {
    const onResize = () => {
      setWindowDimensions(getWindowDimensions());
    }
    window.addEventListener('resize', onResize);

    // DevToolsのデバイスツールバー切り替え等、window の 'resize' イベントが
    // 確実に発火しないケースがあるため、ResizeObserver でも実サイズの変化を検知する
    // （windowWidth を px で直接レイアウトに使うコンポーネントが複数あり、
    // 古い幅のまま固定されるとページ全体が横スクロールしてしまう不具合の対策）
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(document.documentElement);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);


  // 初回言語設定
  useEffect(() => {
    if(langCode == null) {
      // URLが言語を明示している場合（/ja 等）はそれを優先し、なければ端末の言語で決める
      const initLangCode = PATH_LANG_MAP[currentPath] || getBrowserLang();

      // 翻訳データ（i18next-http-backend）は非同期で読み込まれるため、読み込み完了までローディングを表示する
      // （待たずに表示すると、一瞬翻訳キーの文字列が表示される）
      // ただし読み込みが遅い・失敗した時に読み込み中のまま止まらないよう、上限を過ぎたら同梱の英語で表示する
      let isFallback = false;
      const fallbackTimer = setTimeout(() => {
        isFallback = true;
        i18n.changeLanguage('en');
        setLangCode('en');
        // ローディング終了
        setIsLangLoading(false);
      }, LANG_LOAD_TIMEOUT_MS);

      i18n.changeLanguage(initLangCode).finally(() => {
        clearTimeout(fallbackTimer);

        // 読み込みに失敗した場合は英語のまま（英語の時は同梱済みのため常にある）
        if(!i18n.hasResourceBundle(initLangCode, 'translations')) {
          i18n.changeLanguage('en');
          setLangCode('en');
          setIsLangLoading(false);
          return;
        }

        // 英語で表示した後に読み込めた場合も、本来の言語に切り替える（isFallback=true の場合）
        setLangCode(initLangCode);
        // ローディング終了
        setIsLangLoading(false);
      });
    } else {
      // 文字サイズ:ポルトガル語・スペイン語・ポーランド語の場合は文字を小さく
      setIsTxtSmall(langCode=='pt' || langCode=='es'|| langCode=='pl');
    }
  }, [langCode])


  // 表示言語変更
  useEffect(() => {
    // 初回の言語が決まるまでは何もしない（決まる前に英語への切り替え・URLの書き換えが走るのを防ぐ）
    if(langCode == null) return;

    switch(langCode) {
      case 'en':
        // 英語
        i18n.changeLanguage('en');
        // 他トップの場合は英語URL
        if(
          currentPath == routeTopJa ||
          currentPath == routeTopPt ||
          currentPath == routeTopEs ||
          currentPath == routeTopKo ||
          currentPath == routeTopHi ||
          currentPath == routeTopPl
        ) {
          navigate(routeTop);
        }
        break;
      case 'ja':
        // 日本語
        i18n.changeLanguage('ja');
        // 他トップの場合は日本語URL
        if(
          currentPath == routeTop ||
          currentPath == routeTopPt ||
          currentPath == routeTopEs ||
          currentPath == routeTopKo ||
          currentPath == routeTopHi ||
          currentPath == routeTopPl
        ) {
          navigate(routeTopJa);
        }
        break;
      case 'pt':
        // ポルトガル語
        i18n.changeLanguage('pt');
        // 他トップの場合はポルトガル語URL
        if(
          currentPath == routeTop ||
          currentPath == routeTopJa ||
          currentPath == routeTopEs ||
          currentPath == routeTopKo ||
          currentPath == routeTopHi ||
          currentPath == routeTopPl
        ) {
          navigate(routeTopPt);
        }
        break;
      case 'es':
        // スペイン語
        i18n.changeLanguage('es');
        // 他トップの場合はスペイン語URL
        if(
          currentPath == routeTop ||
          currentPath == routeTopJa ||
          currentPath == routeTopPt ||
          currentPath == routeTopKo ||
          currentPath == routeTopHi ||
          currentPath == routeTopPl
        ) {
          navigate(routeTopEs);
        }
        break;
      case 'ko':
        // 韓国語
        i18n.changeLanguage('ko');
        // 他トップの場合は韓国語URL
        if(
          currentPath == routeTop ||
          currentPath == routeTopJa ||
          currentPath == routeTopPt ||
          currentPath == routeTopEs ||
          currentPath == routeTopHi ||
          currentPath == routeTopPl
        ) {
          navigate(routeTopKo);
        }
        break;
      case 'hi':
        // ヒンディー語
        i18n.changeLanguage('hi');
        // 他トップの場合はヒンディー語URL
        if(
          currentPath == routeTop ||
          currentPath == routeTopJa ||
          currentPath == routeTopPt ||
          currentPath == routeTopEs ||
          currentPath == routeTopKo ||
          currentPath == routeTopPl
        ) {
          navigate(routeTopHi);
        }
        break;
      case 'pl':
        // ポーランド語
        i18n.changeLanguage('pl');
        // 他トップの場合はポーランド語URL
        if(
          currentPath == routeTop ||
          currentPath == routeTopJa ||
          currentPath == routeTopPt ||
          currentPath == routeTopEs ||
          currentPath == routeTopKo ||
          currentPath == routeTopHi
        ) {
          navigate(routeTopPl);
        }
        break;
      default:
        // 英語(デフォルト)
        i18n.changeLanguage('en');
        // 他トップの場合は英語URL
        if(
          currentPath == routeTopJa ||
          currentPath == routeTopPt ||
          currentPath == routeTopEs ||
          currentPath == routeTopKo ||
          currentPath == routeTopHi ||
          currentPath == routeTopPl
        ) {
          navigate(routeTop);
        }
    }
  }, [langCode, currentPath])



  return (
    <div className="App">
      <hpContext.Provider value={contextValue}>

        {/* SEO(headタグ内の多言語) */}
        <SeoHeadTag />

        {/* 言語ローディング */}
        {isLangLoading?
          <Loading />
        :
          <>
            <HpHeader />

            <View style={styles.container}>
              <Routes>
                <Route path={routeTop} element={<Top />} />
                <Route path={routeTopJa} element={<Top />} />
                <Route path={routeTopPt} element={<Top />} />
                <Route path={routeTopEs} element={<Top />} />
                <Route path={routeTopKo} element={<Top />} />
                <Route path={routeTopHi} element={<Top />} />
                <Route path={routeTopPl} element={<Top />} />
                <Route path={routeTermOfService} element={<TermOfService />} />
                <Route path={routePrivacyPolicy} element={<PrivacyPolicy />} />
                <Route path={routeTokutei} element={<Tokutei />} />
                <Route path={routeContact} element={<Contact />} />
                <Route path={routeContactDone} element={<ContactDone />} />
                <Route path={routeAppApi} element={<Top isOpenApp={true} />} />
                <Route path="*" element={<Error404 />} />
              </Routes>
            </View>

            <HpFooter />

            <ChangeLanguage />
          </>
        }

      </hpContext.Provider>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1, // フッターを最下部にするため
  },
});

export default Hp;
